import { useState } from 'react';
import { CalculationEditor } from './CalculationEditor';
import type { AnalysisSpec, Dataset } from '@lognai/plugin-analytics';
import { Badge, Checkbox, Input } from '@lognai/synapse-design-system-v2';
import { Search } from '@lognai/synapse-design-system-v2/icons';
import { businessLabel } from '../../lib/user-facing';
import { memberLabel, type CreatorCatalog } from '../creator-helpers';

const calculations: Record<string, string> = {
  sum: 'Sum',
  avg: 'Average',
  count: 'Count',
  countDistinct: 'Distinct count',
  min: 'Minimum',
  max: 'Maximum',
};

export function BuilderFields({
  catalog,
  dataset,
  analysis,
  onChange,
  disabled,
}: {
  catalog?: CreatorCatalog;
  dataset?: Dataset;
  analysis: AnalysisSpec;
  onChange: (patch: Partial<AnalysisSpec>) => void;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const matches = (member: string) =>
    !needle || memberLabel(member, catalog).toLowerCase().includes(needle);
  const toggleMeasure = (member: string) => {
    const measures = analysis.measures.includes(member)
      ? analysis.measures.filter((item) => item !== member)
      : [...analysis.measures, member].slice(0, 5);
    const order = (analysis.order ?? []).filter(
      (entry) =>
        measures.includes(entry.member) ||
        analysis.dimensions.includes(entry.member) ||
        entry.member === analysis.timeDimension?.member
    );
    if (!order.length && measures[0]) order.push({ member: measures[0], direction: 'desc' });
    onChange({ measures, order });
  };
  const toggleDimension = (member: string) => {
    const dimensions = analysis.dimensions.includes(member)
      ? analysis.dimensions.filter((item) => item !== member)
      : [...analysis.dimensions, member].slice(0, 3);
    onChange({
      dimensions,
      drillPath: [
        ...dimensions,
        ...(catalog?.dimensions.filter((item) => !dimensions.includes(item)) ?? []),
      ].slice(0, 5),
      order: (analysis.order ?? []).filter(
        (entry) =>
          !catalog?.dimensions.includes(entry.member) ||
          dimensions.includes(entry.member) ||
          entry.member === analysis.timeDimension?.member
      ),
    });
  };
  const fields = dataset?.fields.filter(
    (field) =>
      field.aggregations.some(
        (aggregation) => catalog?.measures[`${analysis.view}.${field.id}_${aggregation}`]
      ) &&
      (!needle || field.name.toLowerCase().includes(needle))
  );
  const grouped = new Set(
    dataset?.fields.flatMap((field) =>
      field.aggregations.map((aggregation) => `${analysis.view}.${field.id}_${aggregation}`)
    ) ?? []
  );
  const otherMeasures = Object.entries(catalog?.measures ?? {}).filter(
    ([member]) => !grouped.has(member) && matches(member)
  );
  const visibleDimensions = (catalog?.dimensions ?? []).filter(matches);
  const groups = new Map<string, string[]>();
  for (const member of visibleDimensions) {
    const label = memberLabel(member, catalog);
    const related = member.split('.')[1]?.startsWith('r_');
    const group = related ? label.split(':')[0] : (dataset?.name ?? 'Business fields');
    groups.set(group, [...(groups.get(group) ?? []), member]);
  }
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-3 text-muted-foreground"
        />
        <Input
          aria-label="Search metrics and breakdowns"
          className="pl-9"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search metrics and breakdowns"
        />
      </div>
      <CalculationEditor analysis={analysis} catalog={catalog} onChange={onChange} disabled={disabled} />
      <section className="space-y-2">
        <div className="flex justify-between text-xs">
          <h3 className="font-semibold">Metrics</h3>
          <span className="text-muted-foreground">{analysis.measures.length}/5 selected</span>
        </div>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {fields?.map((field) => (
            <div key={field.id} className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium">{field.name}</span>
                <Badge variant="outline">
                  {businessLabel(field.type)}
                  {field.currency || field.unit ? ` · ${field.currency ?? field.unit}` : ''}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {field.aggregations.map((aggregation) => {
                  const member = `${analysis.view}.${field.id}_${aggregation}`;
                  if (!catalog?.measures[member]) return null;
                  return (
                    <label
                      key={member}
                      className="flex cursor-pointer items-center gap-1.5 text-xs"
                    >
                      <Checkbox
                        aria-label={`${field.name} (${calculations[aggregation] ?? aggregation})`}
                        checked={analysis.measures.includes(member)}
                        disabled={
                          disabled ||
                          (!analysis.measures.includes(member) && analysis.measures.length >= 5)
                        }
                        onCheckedChange={() => toggleMeasure(member)}
                      />
                      {calculations[aggregation] ?? aggregation}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          {otherMeasures.map(([member, info]) => (
            <label
              key={member}
              className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-border px-3 text-xs"
            >
              <Checkbox
                aria-label={info.label}
                checked={analysis.measures.includes(member)}
                disabled={
                  disabled || (!analysis.measures.includes(member) && analysis.measures.length >= 5)
                }
                onCheckedChange={() => toggleMeasure(member)}
              />
              <span className="min-w-0 flex-1">{info.label}</span>
              <Badge variant="outline">{info.currency ?? info.unit}</Badge>
            </label>
          ))}
          {!fields?.length && !otherMeasures.length && (
            <p className="text-xs text-muted-foreground">No matching metrics.</p>
          )}
        </div>
      </section>
      <section className="space-y-2">
        <div className="flex justify-between text-xs">
          <h3 className="font-semibold">Break down by</h3>
          <span className="text-muted-foreground">{analysis.dimensions.length}/3 selected</span>
        </div>
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {[...groups].map(([group, members]) => (
            <div key={group} className="space-y-1">
              <p className="py-1 text-[11px] font-medium text-muted-foreground">
                {group}
                {group !== (dataset?.name ?? 'Business fields') ? ' · Approved relationship' : ''}
              </p>
              {members.map((member) => {
                const field = dataset?.fields.find(
                  (item) => `${analysis.view}.${item.id}` === member
                );
                return (
                  <label
                    key={member}
                    className="flex min-h-9 cursor-pointer items-center gap-2 rounded-lg border border-border px-3 text-xs"
                  >
                    <Checkbox
                      aria-label={`Break down by ${memberLabel(member, catalog)}`}
                      checked={analysis.dimensions.includes(member)}
                      disabled={
                        disabled ||
                        (!analysis.dimensions.includes(member) && analysis.dimensions.length >= 3)
                      }
                      onCheckedChange={() => toggleDimension(member)}
                    />
                    <span className="min-w-0 flex-1">{memberLabel(member, catalog)}</span>
                    {field && (
                      <span className="text-[10px] text-muted-foreground">
                        {businessLabel(field.type)}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          ))}
          {!visibleDimensions.length && (
            <p className="text-xs text-muted-foreground">No matching breakdowns.</p>
          )}
        </div>
      </section>
    </div>
  );
}
